/**
 * Data Detector - Detect structured data (CSV, JSON, tables) from AI responses
 * and determine the best chart type for visualization.
 */

export interface DetectedData {
  type: 'json' | 'csv' | 'markdown_table';
  labels: string[];
  datasets: DatasetEntry[];
  suggestedChartType: ChartType;
  rawData: string;
  startIndex: number;
  endIndex: number;
}

export interface DatasetEntry {
  label: string;
  values: (number | null)[];
}

export type ChartType = 'bar' | 'line' | 'pie' | 'stacked_bar';

/**
 * Detect all structured data blocks from AI response text
 */
export function detectDataInText(text: string): DetectedData[] {
  const results: DetectedData[] = [];

  // 1. Detect chart_data JSON blocks
  const jsonResults = detectChartDataJson(text);
  results.push(...jsonResults);

  // 2. Detect CSV blocks
  const csvResults = detectCsvBlocks(text);
  results.push(...csvResults);

  // 3. Detect markdown tables
  const tableResults = detectMarkdownTables(text);
  results.push(...tableResults);

  return results;
}

/**
 * Detect chart_data JSON blocks (from analysis templates)
 */
function detectChartDataJson(text: string): DetectedData[] {
  const results: DetectedData[] = [];
  const pattern = /```json\s*\n?\s*(\{[\s\S]*?"chart_data"[\s\S]*?\})\s*\n?\s*```/gm;

  let match;
  while ((match = pattern.exec(text)) !== null) {
    try {
      const parsed = JSON.parse(match[1]);
      const chartData = parsed.chart_data || parsed;

      if (!chartData.labels || !Array.isArray(chartData.labels)) continue;

      const datasets: DatasetEntry[] = [];

      if (chartData.values && Array.isArray(chartData.values)) {
        datasets.push({
          label: 'Value',
          values: chartData.values.map((v: any) => (typeof v === 'number' ? v : null)),
        });
      }

      if (chartData.datasets && Array.isArray(chartData.datasets)) {
        for (const ds of chartData.datasets) {
          datasets.push({
            label: ds.label || 'Dataset',
            values: (ds.values || []).map((v: any) => (typeof v === 'number' ? v : null)),
          });
        }
      }

      if (datasets.length > 0) {
        results.push({
          type: 'json',
          labels: chartData.labels,
          datasets,
          suggestedChartType: mapChartType(chartData.type),
          rawData: match[1],
          startIndex: match.index,
          endIndex: match.index + match[0].length,
        });
      }
    } catch {
      // Skip invalid JSON
    }
  }

  return results;
}

/**
 * Detect CSV data blocks (within code blocks or plain CSV)
 */
function detectCsvBlocks(text: string): DetectedData[] {
  const results: DetectedData[] = [];
  const pattern = /```(?:csv)?\s*\n([\s\S]*?)\n```/gm;

  let match;
  while ((match = pattern.exec(text)) !== null) {
    const csvContent = match[1].trim();
    const parsed = parseCsv(csvContent);

    if (parsed) {
      results.push({
        ...parsed,
        type: 'csv',
        rawData: csvContent,
        startIndex: match.index,
        endIndex: match.index + match[0].length,
      });
    }
  }

  return results;
}

/**
 * Detect markdown tables in text
 */
function detectMarkdownTables(text: string): DetectedData[] {
  const results: DetectedData[] = [];
  // Match markdown table patterns: | header | header |\n|---|---|\n| data | data |
  const pattern = /(\|[^\n]+\|\n\|[\s\-:|]+\|\n(?:\|[^\n]+\|\n?)+)/gm;

  let match;
  while ((match = pattern.exec(text)) !== null) {
    const tableText = match[1].trim();
    const parsed = parseMarkdownTable(tableText);

    if (parsed) {
      results.push({
        ...parsed,
        type: 'markdown_table',
        rawData: tableText,
        startIndex: match.index,
        endIndex: match.index + match[0].length,
      });
    }
  }

  return results;
}

/**
 * Parse CSV string into DetectedData fields
 */
function parseCsv(csv: string): Omit<DetectedData, 'type' | 'rawData' | 'startIndex' | 'endIndex'> | null {
  const lines = csv.split('\n').filter(l => l.trim());
  if (lines.length < 2) return null;

  const delimiter = lines[0].includes('\t') ? '\t' : ',';
  const headers = lines[0].split(delimiter).map(h => h.trim().replace(/^"|"$/g, ''));
  if (headers.length < 2) return null;

  const numericColumns: number[] = [];
  const dataRows = lines.slice(1);

  // Determine which columns are numeric
  for (let col = 1; col < headers.length; col++) {
    const isNumeric = dataRows.every(row => {
      const cells = row.split(delimiter);
      const val = cells[col]?.trim().replace(/^"|"$/g, '').replace(/,/g, '');
      return val === '' || val === '-' || !isNaN(Number(val));
    });
    if (isNumeric) numericColumns.push(col);
  }

  if (numericColumns.length === 0) return null;

  const labels = dataRows.map(row => {
    const cells = row.split(delimiter);
    return cells[0]?.trim().replace(/^"|"$/g, '') || '';
  });

  const datasets: DatasetEntry[] = numericColumns.map(col => ({
    label: headers[col],
    values: dataRows.map(row => {
      const cells = row.split(delimiter);
      const val = cells[col]?.trim().replace(/^"|"$/g, '').replace(/,/g, '');
      if (!val || val === '-') return null;
      const num = Number(val);
      return isNaN(num) ? null : num;
    }),
  }));

  return {
    labels,
    datasets,
    suggestedChartType: suggestChartType(labels, datasets),
  };
}

/**
 * Parse markdown table into DetectedData fields
 */
function parseMarkdownTable(table: string): Omit<DetectedData, 'type' | 'rawData' | 'startIndex' | 'endIndex'> | null {
  const lines = table.split('\n').filter(l => l.trim());
  if (lines.length < 3) return null; // header + separator + at least 1 data row

  const parseRow = (line: string): string[] =>
    line.split('|').filter(c => c.trim()).map(c => c.trim());

  const headers = parseRow(lines[0]);
  if (headers.length < 2) return null;

  // Skip separator line (line[1])
  const dataLines = lines.slice(2);
  if (dataLines.length === 0) return null;

  const numericColumns: number[] = [];

  for (let col = 1; col < headers.length; col++) {
    const isNumeric = dataLines.every(line => {
      const cells = parseRow(line);
      const val = cells[col]?.replace(/,/g, '').replace(/%$/, '').replace(/円$/, '');
      return !val || val === '-' || !isNaN(Number(val));
    });
    if (isNumeric) numericColumns.push(col);
  }

  if (numericColumns.length === 0) return null;

  const labels = dataLines.map(line => parseRow(line)[0] || '');

  const datasets: DatasetEntry[] = numericColumns.map(col => ({
    label: headers[col],
    values: dataLines.map(line => {
      const cells = parseRow(line);
      const val = cells[col]?.replace(/,/g, '').replace(/%$/, '').replace(/円$/, '');
      if (!val || val === '-') return null;
      const num = Number(val);
      return isNaN(num) ? null : num;
    }),
  }));

  return {
    labels,
    datasets,
    suggestedChartType: suggestChartType(labels, datasets),
  };
}

/**
 * Suggest the best chart type based on data characteristics
 */
function suggestChartType(labels: string[], datasets: DatasetEntry[]): ChartType {
  const datasetCount = datasets.length;
  const labelCount = labels.length;

  // Time-series data -> line chart
  const isTimeSeries = labels.some(l =>
    /\d{4}[\/\-]\d{1,2}/.test(l) || /月|年|Q[1-4]|期/.test(l)
  );
  if (isTimeSeries) return 'line';

  // Few categories with single dataset -> pie chart
  if (labelCount <= 8 && datasetCount === 1) {
    const hasNegative = datasets[0].values.some(v => v !== null && v < 0);
    if (!hasNegative) return 'pie';
  }

  // Multiple datasets -> stacked bar or grouped bar
  if (datasetCount > 1) return 'bar';

  // Default -> bar chart
  return 'bar';
}

/**
 * Map string chart type to our ChartType
 */
function mapChartType(type: string): ChartType {
  switch (type?.toLowerCase()) {
    case 'line': return 'line';
    case 'pie': return 'pie';
    case 'stacked_bar': return 'stacked_bar';
    case 'bar':
    default:
      return 'bar';
  }
}
